import React, { useState, useEffect } from 'react';
import './TagReportsTab.css';

// Reports filed from the mobile app when a user scanned a tag that wasn't part of their school.
// Shows what was written to the tag plus the serial they read off the physical label, so staff can
// reconcile a mis-scanned / foreign tag.
function TagReportsTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const authHeaders = () => {
    const username = localStorage.getItem('admin_username');
    const password = localStorage.getItem('admin_password');
    return {
      'Content-Type': 'application/json',
      'Authorization': 'Basic ' + btoa(`${username}:${password}`)
    };
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const response = await fetch(`${apiEndpoint}/admin/tag-reports`, { headers: authHeaders(), cache: 'no-store' });
      if (response.ok) setReports(await response.json());
    } catch (err) {
      console.error('Error loading tag reports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const res = await fetch(`${apiEndpoint}/admin/tag-reports/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      if (res.ok) setReports(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch (err) {
      console.error('Error updating tag report:', err);
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const visible = reports.filter(r => showResolved || r.status !== 'resolved');
  const newCount = reports.filter(r => r.status !== 'resolved').length;

  return (
    <div className="tag-reports">
      <div className="tag-reports-header">
        <div>
          <h2>Tag reports</h2>
          <p className="tag-reports-sub">
            Scanned tags that weren't in the scanner's school: {newCount} open
          </p>
        </div>
        <div className="tag-reports-actions">
          <label className="tag-reports-toggle">
            <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} />
            Show resolved
          </label>
          <button className="tag-reports-refresh" onClick={fetchReports} disabled={loading}>
            {loading ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {loading && reports.length === 0 ? (
        <p className="tag-reports-empty">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="tag-reports-empty">No tag reports{showResolved ? '' : ' open'} right now.</p>
      ) : (
        <div className="tag-reports-table-wrap">
          <table className="tag-reports-table">
            <thead>
              <tr>
                <th>Reported</th>
                <th>Physical serial</th>
                <th>Tag serial</th>
                <th>Tag product</th>
                <th>School</th>
                <th>Reported by</th>
                <th>Note</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => (
                <tr key={r.id} className={r.status === 'resolved' ? 'resolved-row' : ''}>
                  <td className="nowrap">{fmtDate(r.createdAt)}</td>
                  <td className="mono strong">{r.physicalSerial || '-'}</td>
                  <td className="mono">{r.tagSerial || '-'}</td>
                  <td>{r.tagSku || '-'}</td>
                  <td>{r.schoolName || '-'}</td>
                  <td>
                    {r.reportedByName || '-'}
                    {r.reportedByEmail ? <div className="muted">{r.reportedByEmail}</div> : null}
                  </td>
                  <td className="note-cell">{r.note || ''}</td>
                  <td className="nowrap">
                    {r.status === 'resolved' ? (
                      <button className="link-btn" disabled={busyId === r.id} onClick={() => setStatus(r.id, 'new')}>
                        Reopen
                      </button>
                    ) : (
                      <button className="resolve-btn" disabled={busyId === r.id} onClick={() => setStatus(r.id, 'resolved')}>
                        {busyId === r.id ? '…' : 'Resolve'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TagReportsTab;
